import { useEffect, useState } from 'react'
import { Button, Modal, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/format'
import { formatMinutes } from '../format'
import { useCaseOptions, useDeleteTimeEntry, useUpdateTimeEntry } from '../hooks'
import type { TimeEntry } from '../types'
import type { TimeEntryFormState } from './TimeEntryFormFields'
import { TimeEntryFormFields, combineMinutes, splitMinutes } from './TimeEntryFormFields'

export interface EditTimeEntryModalProps {
  entry: TimeEntry | null
  onClose: () => void
  /** `false` → podgląd bez zapisu (brak `timesheet.log`). */
  canEdit: boolean
}

/** Edycja / usunięcie wpisu czasu. Usunięcie potwierdza się DRUGIM kliknięciem. */
export function EditTimeEntryModal({ entry, onClose, canEdit }: EditTimeEntryModalProps) {
  const toast = useToast()
  const cases = useCaseOptions(Boolean(entry) && canEdit)
  const update = useUpdateTimeEntry()
  const remove = useDeleteTimeEntry()

  const [form, setForm] = useState<TimeEntryFormState>({
    caseId: '',
    workDate: '',
    hours: '',
    minutes: '',
    description: '',
    billable: true,
  })
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!entry) return
    const { hours, minutes } = splitMinutes(entry.minutes)
    setForm({
      caseId: String(entry.case_id),
      workDate: entry.work_date,
      hours,
      minutes,
      description: entry.description ?? '',
      billable: entry.billable,
    })
    setConfirmDelete(false)
    setError(null)
  }, [entry])

  if (!entry) return null

  const save = async () => {
    setError(null)
    const total = combineMinutes(form.hours, form.minutes)
    if (!form.caseId || !form.workDate) {
      setError('Podaj sprawę i datę.')
      return
    }
    if (total === null) {
      setError('Podaj czas w zakresie od 1 minuty do 24 godzin.')
      return
    }
    try {
      await update.mutateAsync({
        id: entry.id,
        case_id: Number(form.caseId),
        work_date: form.workDate,
        minutes: total,
        description: form.description.trim() ? form.description.trim() : null,
        billable: form.billable,
      })
      toast.success('Wpis zapisany')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się zapisać wpisu.')
    }
  }

  const destroy = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setError(null)
    try {
      await remove.mutateAsync(entry.id)
      toast.success('Wpis usunięty')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się usunąć wpisu.')
    }
  }

  const busy = update.isPending || remove.isPending

  return (
    <Modal
      open
      onClose={onClose}
      title="Wpis czasu"
      subtitle={[
        entry.case ? `${entry.case.signature} · ${entry.case.client_name}` : null,
        formatDate(entry.work_date),
        formatMinutes(entry.minutes),
      ]
        .filter(Boolean)
        .join(' · ')}
      footer={
        <>
          {canEdit ? (
            <Button variant="secondary" onClick={destroy} loading={remove.isPending} disabled={busy}>
              {confirmDelete ? 'Na pewno usunąć?' : 'Usuń'}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {canEdit ? 'Anuluj' : 'Zamknij'}
          </Button>
          {canEdit ? (
            <Button variant="primary" onClick={save} loading={update.isPending} disabled={busy}>
              Zapisz
            </Button>
          ) : null}
        </>
      }
    >
      <TimeEntryFormFields
        form={form}
        onChange={setForm}
        cases={cases.data?.items ?? []}
        disabled={!canEdit}
      />
      {error ? <p className="mt-3 text-micro text-danger">{error}</p> : null}
    </Modal>
  )
}
